/**
 * Functional Age Service
 *
 * Read-only access to snapshot documents written by backend Cloud Functions.
 * Frontend MUST NOT write to, update, or delete snapshot documents.
 *
 * Collection: functionalAgeSnapshots/{userId}/weeks/{snapshotId}
 *
 * IMPORTANT — Field name alignment for Alingga's backend:
 *   Frontend stores vitals in bodyStatsEntries as:
 *     - restingHR (number, bpm)
 *     - bloodPressure: { systolic: number, diastolic: number, pulse?: number }
 *   These are also mirrored to bodyMetrics collection with the same field names.
 *   Backend MUST read these exact field names, NOT restingHeartRate / systolicBP / diastolicBP.
 */

import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseApp } from '@/services/firebase/firebase.config';
import { getFirebaseIdToken } from '@/utils/firebaseToken';
import type { FunctionalAgeSnapshot } from '../types/functionalAge';

const db = getFirestore(firebaseApp);

function weeksCollection(userId: string) {
  return collection(db, 'functionalAgeSnapshots', userId, 'weeks');
}

/** Convert Firestore Timestamp fields to millis for safe frontend use */
function normalizeSnapshot(raw: Record<string, unknown>): FunctionalAgeSnapshot {
  const { createdAt, ...rest } = raw;
  let createdAtMillis: number | null = null;
  if (createdAt != null) {
    if (typeof (createdAt as any).toMillis === 'function') {
      createdAtMillis = (createdAt as any).toMillis();
    } else if (typeof createdAt === 'number') {
      createdAtMillis = createdAt;
    }
  }
  return { ...rest, createdAtMillis } as FunctionalAgeSnapshot;
}

/**
 * Get the most recent snapshot for a user.
 * Returns null if no snapshots exist.
 */
export async function getLatestSnapshot(
  userId: string,
): Promise<FunctionalAgeSnapshot | null> {
  const q = query(
    weeksCollection(userId),
    orderBy('periodEnd', 'desc'),
    limit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return normalizeSnapshot(snap.docs[0].data());
}

/**
 * Get the previous (second-most-recent) snapshot for a user.
 * Used to calculate per-component deltas (current vs previous).
 * Returns null if fewer than 2 snapshots exist.
 */
export async function getPreviousSnapshot(
  userId: string,
): Promise<FunctionalAgeSnapshot | null> {
  const q = query(
    weeksCollection(userId),
    orderBy('periodEnd', 'desc'),
    limit(2),
  );
  const snap = await getDocs(q);
  if (snap.docs.length < 2) return null;
  return normalizeSnapshot(snap.docs[1].data());
}

/**
 * Get snapshot history for the trend graph.
 * Returns snapshots in chronological order (oldest first).
 *
 * @param userId - The user's ID
 * @param historyLimit - Max number of snapshots to fetch (default 12)
 */
export async function getSnapshotHistory(
  userId: string,
  historyLimit = 12,
): Promise<FunctionalAgeSnapshot[]> {
  const q = query(
    weeksCollection(userId),
    orderBy('periodEnd', 'desc'),
    limit(historyLimit),
  );
  const snap = await getDocs(q);
  const snapshots = snap.docs.map((doc) => normalizeSnapshot(doc.data()));
  // Reverse so oldest is first — UI expects chronological order
  return snapshots.reverse();
}

// ── Recompute meta types ────────────────────────────────────────

export interface RecomputeMeta {
  phase: 'early' | 'established';
  nextAvailableAtMillis: number;
  cooldownHours: number;
  remainingComputesToday: number;
  firstFaComputeAtMillis: number | null;
  triggerSource?: 'user' | 'event:workout' | 'event:bodyStats';
}

// ── Trigger compute ─────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export type TriggerSource = 'user' | 'event:workout' | 'event:bodyStats';

export interface ComputeResult {
  status: 'ok' | 'rate_limited' | 'error';
  /** Snapshot returned directly from the response body — avoids Firestore re-fetch race */
  snapshot?: FunctionalAgeSnapshot;
  /** Throttle metadata for UI (cooldown timer, phase cue, etc.) */
  recomputeMeta?: RecomputeMeta;
  /** Reason for rate limit (only present when status === 'rate_limited') */
  rateLimitReason?: 'in_progress' | 'cooldown' | 'daily_cap';
}

/**
 * Trigger a user-scoped FA computation via POST /v1/functional-age/compute-mine.
 * Returns the snapshot directly from the response body to avoid Firestore
 * index propagation lag on the first read-after-write.
 *
 * @param triggerSource - What triggered this compute (default: 'user' for manual)
 */
export async function triggerComputeMine(
  triggerSource: TriggerSource = 'user',
): Promise<ComputeResult> {
  // Demo mode: block backend compute calls
  const { isDemoMode } = await import('../demo');
  if (isDemoMode()) {
    return { status: 'error' };
  }

  try {
    const token = await getFirebaseIdToken();
    const res = await fetch(`${API_BASE}/v1/functional-age/compute-mine`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ triggerSource }),
    });

    if (res.status === 429) {
      const body = await res.json().catch(() => ({}));
      return {
        status: 'rate_limited',
        recomputeMeta: body.recomputeMeta ?? undefined,
        rateLimitReason: body.reason ?? undefined,
      };
    }
    if (!res.ok) return { status: 'error' };

    const body = await res.json();
    return {
      status: 'ok',
      snapshot: body.snapshot ?? undefined,
      recomputeMeta: body.recomputeMeta ?? undefined,
    };
  } catch (err) {
    console.error('[triggerComputeMine] Failed:', err);
    return { status: 'error' };
  }
}

// ── Recompute status (read-only) ────────────────────────────────

/**
 * Fetch current throttle metadata without triggering a compute.
 * Used to initialise the manual recompute button state on mount.
 */
export async function fetchRecomputeStatus(): Promise<RecomputeMeta | null> {
  // Demo mode: no backend calls
  const { isDemoMode } = await import('../demo');
  if (isDemoMode()) {
    return null;
  }

  try {
    const token = await getFirebaseIdToken();
    const res = await fetch(`${API_BASE}/v1/functional-age/recompute-status`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) return null;

    const body = await res.json();
    return body.recomputeMeta ?? null;
  } catch (err) {
    console.error('[fetchRecomputeStatus] Failed:', err);
    return null;
  }
}

// ── Fire-and-forget event trigger (called from workout/bodyStats saves) ──

/**
 * Non-blocking FA recompute trigger for event-driven updates.
 * Called after workout saves and body stats updates.
 * Silently swallows all errors — must never break the parent save flow.
 *
 * Client-side debounce (2s) + in-flight lock prevents fan-out:
 * e.g. 6 rapid body stats saves → 1 compute-mine call.
 */
let _faInFlight = false;
let _faDebounceTimer: ReturnType<typeof setTimeout> | null = null;

export function triggerFaRecomputeInBackground(
  _userId: string,
  triggerSource: TriggerSource,
): void {
  // Already in-flight — skip (backend is processing)
  if (_faInFlight) {
    console.log(`[FA-EVENT] Skipped — already in-flight (trigger: ${triggerSource})`);
    return;
  }

  // Coalesce: reset timer on each call, fire 2s after last trigger
  if (_faDebounceTimer) clearTimeout(_faDebounceTimer);
  _faDebounceTimer = setTimeout(() => {
    _faDebounceTimer = null;
    _faInFlight = true;

    triggerComputeMine(triggerSource).then((result) => {
      if (result.status === 'ok') {
        console.log(`[FA-EVENT] Background recompute succeeded (trigger: ${triggerSource})`);
      } else if (result.status === 'rate_limited') {
        console.log(`[FA-EVENT] Recompute skipped — ${result.rateLimitReason ?? 'throttled'} (trigger: ${triggerSource})`);
      }
    }).catch((err) => {
      console.error(`[FA-EVENT] Background recompute failed:`, err);
    }).finally(() => {
      _faInFlight = false;
    });
  }, 2000);
}
